import * as React from 'react';
import { INLINE_COPY_HOST_CLASS } from '@/hen-ui/lib/inline-copy';

/** `useLayoutEffect` in the browser; `useEffect` on the server to avoid SSR warnings. */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

/**
 * Shared hook for "Read More" / "Read less" content that is clamped to N lines.
 * Measures the clamped element via ref and tracks whether content is truncated;
 * supports toggling expanded state.
 *
 * Use with LineClamp: pass returned ref to LineClamp, and use
 * lines={expanded ? 'none' : 3}. Show the toggle button when expanded || isTruncated.
 */
export function useExpandableClamp(content: string) {
  const [expanded, setExpanded] = React.useState(false);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const expandedRef = React.useRef(expanded);
  expandedRef.current = expanded;

  const checkTruncation = React.useCallback(() => {
    const el = ref.current;
    if (!el || expandedRef.current) return;
    // Line clamp sits on this element, but a nested summary (Text, or inline
    // copy inside it) holds the actual line boxes. Measure those too, or a
    // visually cut summary never counts as truncated.
    const nested = Array.from(
      el.querySelectorAll<HTMLElement>(`.${INLINE_COPY_HOST_CLASS}`),
    );
    setIsTruncated(
      [el, ...nested].some((node) => node.scrollHeight > node.clientHeight),
    );
  }, []);

  // Reset expansion when content changes (e.g. same instance, different item).
  useIsomorphicLayoutEffect(() => {
    setExpanded(false);
    setIsTruncated(false);
  }, [content]);

  React.useEffect(() => {
    checkTruncation();
  }, [content, expanded, checkTruncation]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(checkTruncation);
    ro.observe(el);
    el.querySelectorAll(`.${INLINE_COPY_HOST_CLASS}`).forEach((node) =>
      ro.observe(node),
    );
    return () => ro.disconnect();
  }, [checkTruncation]);

  const toggleExpanded = React.useCallback(
    () => setExpanded((prev) => !prev),
    [],
  );

  return {
    ref,
    expanded,
    isTruncated,
    setExpanded,
    toggleExpanded,
  };
}
